import { PropsWithChildren } from "react";
import { useForm } from "react-hook-form";
import { uniqBy } from "lodash";
import { Box, Button, SelectProps, Skeleton, Stack, css } from "@mui/material";
import { ModalProps, useModal, createUseModal } from "@imminently/react/context/modals";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import { FormInputSelect } from "@imminently/react/ui/forms";

import { Theme } from "@/theme";
import { userService } from "@/services";
import { formatName } from "@/global";
import { useAppDispatch } from "@/store";
import { Icon } from "@/components/Icon";
import { clearAdvancedFilters, setAdvancedFilters, usePatientList } from "./patient-slice";

const styles = {
  root: (theme: Theme) => css`
  `,
  content: (theme: Theme) => css`
    flex: 1;
    width: 34rem;
  `,
  selectChevron: {
    right: "0.875rem",
    top: "calc(50% - 0.75rem)",
    color: "inherit",
  },
};

type InputWrapperProps = { label: string } & PropsWithChildren;

const InputWrapper = (props: InputWrapperProps) => {
  const { label, children } = props;

  return (
    <Stack flex={1} gap={1}>
      <Text bold>{label}</Text>
      {children}
    </Stack>
  );
};

const AGE_RANGE_OPTIONS = [
  { label: "None", value: "" },
  { label: "0-10", value: "0-10" },
  { label: "11-20", value: "11-20" },
  { label: "21-30", value: "21-30" },
  { label: "31-40", value: "31-40" },
  { label: "41-50", value: "41-50" },
  { label: "51-60", value: "51-60" },
  { label: "61-70", value: "61-70" },
  { label: "71-80", value: "71-80" },
  { label: "81-90", value: "81-90" },
  { label: "90+", value: "91-200" },
];

const selectProps: Partial<SelectProps> = {
  IconComponent: (props: any) => <Icon icon="chevrondown" {...props} style={styles.selectChevron} />,
};

export interface AdvancedFiltersModalProps { }

export const AdvancedFiltersModal = ({ props }: ModalProps<AdvancedFiltersModalProps>) => {
  const { popModal: close } = useModal();
  const dispatch = useAppDispatch();

  const { advancedFilters } = usePatientList();
  const { data, isLoading } = userService.useGetList();

  const { control, handleSubmit } = useForm({
    defaultValues: advancedFilters,
  });

  const clearFilters = () => {
    dispatch(clearAdvancedFilters());
    close();
  };

  const onSubmit = handleSubmit((formData: any) => {
    console.log(formData);
    dispatch(setAdvancedFilters(formData));
    close();
  });

  const users = uniqBy((data?.data || []), (u) => u.identity_id).map((u) => ({ label: formatName(u.firstName, u.lastName), value: u.identity_id }));

  return (
    <Dialog open onClose={close} maxWidth="sm">
      <DialogTitle>
        <Text variant="h3">Advanced filters</Text>
      </DialogTitle>
      <DialogContent css={styles.content}>
        <Stack pt={2}>
          <Stack direction="row" gap={3}>
            <InputWrapper label="Age range">
              <FormInputSelect fullWidth SelectProps={selectProps} control={control} name="ageRange" options={AGE_RANGE_OPTIONS} placeholder="Select age range" />
            </InputWrapper>
            <InputWrapper label="Senior Doctor">
              {
                isLoading
                  ? <Skeleton variant="rounded" width="100%" height="2.5rem" />
                  : <FormInputSelect fullWidth SelectProps={selectProps} control={control} name="snrDoctor" options={users} />
              }
            </InputWrapper>
          </Stack>
          <Stack direction="row" gap={3}>
            <InputWrapper label="Doctor">
              {
                isLoading
                  ? <Skeleton variant="rounded" width="100%" height="2.5rem" />
                  : <FormInputSelect fullWidth SelectProps={selectProps} control={control} name="doctor" options={users} />
              }
            </InputWrapper>
            <InputWrapper label="Nurse">
              {
                isLoading
                  ? <Skeleton variant="rounded" width="100%" height="2.5rem" />
                  : <FormInputSelect fullWidth SelectProps={selectProps} control={control} name="nurse" options={users} />
              }
            </InputWrapper>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={clearFilters}>Clear filters</Button>
        <Box flex={1} />
        <Button variant="outlined" onClick={close}>Cancel</Button>
        <Button color="success" onClick={onSubmit}>Apply filters</Button>
      </DialogActions>
    </Dialog>
  );
};

export const useAdvancedFiltersModal = createUseModal(AdvancedFiltersModal);